import { Modal } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import "./addOnsModal.scss";
import Button from "../command/Button/Button";
import { X } from "react-feather";
import { productDetailsClient } from "../../data/product-details";
import { Product, AddonProductListResponse, AddonProductsRequest } from "../../interfaces/product.interface";
import { PlaceHolderStoreImage } from "../../assets/svgImages/svgImages";
import { getAuthCredentials, setQuoteCookies } from "../../utils/auth-utils";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import moment from "moment";


interface AddOnsModalProps {
  show: boolean;
  onHide: () => void;
  onAddToCart?: (productId: string) => void;
  quoteProductData?: any;
  addOns?: any[];
  productuuid?: string;
}

const AddOnsModal: React.FC<AddOnsModalProps> = ({
  show,
  onHide,
  onAddToCart,
  quoteProductData,
  productuuid,
  addOns = []
}) => {
  const navigate = useNavigate();

  const [modalSize, setModalSize] = useState<"sm" | "lg" | "xl" | undefined>("lg");
  const [recommendedProducts, setRecommendedProducts] = useState<AddonProductListResponse | null>(null);
  const [selectedAddOn, setSelectedAddOn] = useState<Product[]>([]);
  const { quoteId } = getAuthCredentials();
  const [loading, setLoading] = useState<boolean>(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const loaderRef = React.useRef<HTMLDivElement>(null);
  const handleResize = () => {
    if (window.innerWidth < 576) {
      setModalSize("sm");
    } else if (window.innerWidth < 1399) {
      setModalSize("lg");
    } else {
      setModalSize(undefined);
    }
  };
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize(); // Initial call to set the size based on the initial window size

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Reset all state when modal is hidden
  useEffect(() => {
    if (!show) {
      setRecommendedProducts(null);
      setSelectedAddOn([]);
    }
  }, [show]);

  // Use IntersectionObserver for infinite scrolling
  useEffect(() => {
    // Skip if we're already loading, there are no more products, or no loader element
    if (loading || !recommendedProducts?.hasMore || !loaderRef.current) return;

    const loadMoreItems = () => {
      if (!recommendedProducts?.hasMore || loading || !quoteProductData) return;

      // Get the last item's createdAt timestamp
      const lastItem = recommendedProducts.productsList[recommendedProducts.productsList.length - 1];
      if (lastItem && lastItem.createdAt) {
        const data: AddonProductsRequest = {
          storeUuid: quoteProductData.storeUuid,
          quoteUuid: quoteProductData.quoteUuid,
          rentalType: quoteProductData.rentalType,
          bookingStartDate: quoteProductData.bookingStartDate,
          bookingEndDate: quoteProductData.bookingEndDate,
          lastCreatedAt: lastItem.createdAt
        };
        getRecommendedProducts(data, true);
      }
    };

    // Create an observer instance
    const observer = new IntersectionObserver((entries) => {
      // If the loader is visible
      if (entries[0].isIntersecting) {
        loadMoreItems();
      }
    }, { threshold: 0.1 });

    // Start observing the loader element
    observer.observe(loaderRef.current);

    // Cleanup
    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [recommendedProducts, loading, quoteProductData]);


  const getRecommendedProducts = (data: AddonProductsRequest, append: boolean = false) => {
    setLoading(true);
    productDetailsClient.getAddonProducts(data)
      .then((response: any) => {
        if (append && recommendedProducts) {
          // Append new products to existing list
          setRecommendedProducts({
            ...response?.data,
            productsList: [...recommendedProducts.productsList, ...response?.data.productsList]
          });
        } else {
          // Set initial products
          setRecommendedProducts(response?.data);
        }
      })
      .catch((error: any) => {
        console.error('Error fetching recommended products:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }


  useEffect(() => {

    if (quoteProductData) {      
      const data: AddonProductsRequest = {
        storeUuid: quoteProductData.storeUuid,
        quoteUuid: quoteProductData.quoteUuid,
        rentalType: quoteProductData.rentalType,
        bookingStartDate: quoteProductData.bookingStartDate,
        bookingEndDate: quoteProductData.bookingEndDate,
        baseProductUuidForAddOn: quoteProductData.value.products.find((row: any) => row.isAddOn === false)?.productUuid ?? productuuid
      };
      getRecommendedProducts(data);
    }
  }, [quoteProductData]);
  // Sample add-on products (replace with actual data


  const onSubmit = async (redirectToCart: boolean = true, productUuid: string = '') => {
    if (selectedAddOn.length === 0) {
      productDetailsClient
        .createQuote(quoteProductData.value)
        .then((res: any) => {
          setQuoteCookies(res.quote.uuid);
          const bookingDate = JSON.stringify({
            startDate: moment.parseZone(quoteProductData.startDate).format('YYYY-MM-DDTHH:mm:ss'),
            endDate: moment.parseZone(quoteProductData.endDate).format('YYYY-MM-DDTHH:mm:ss'),
            rentPrice: quoteProductData.rentPrice,
          });          
          localStorage.setItem("bookingDate", bookingDate);
          onHide();
          if (redirectToCart) {
            navigate("/cart");
          } else {
            navigate(`/product?id=${productUuid}`);
          }

        })
        .catch((error: any) => {
          console.log(error)
          toast.error(error?.message);
        });
    } else {
      const bookingDate = quoteProductData.value.products[0].bookingDate;
      // Start with the original products from quoteProductData
      const baseProducts = quoteProductData.value.products.map((item: any) => ({
        bookingDate,
        productUuid: item.productUuid,
        productVariantUuid: item.productVariantUuid,
        productRetailValue: item.productRetailValue,
        quantity: item.productQuantity,
        rentalDuration: quoteProductData.value.rentalDuration,
        rentalType: quoteProductData.rentalType,
        retailerUuid: quoteProductData.value.retailerUuid,
        storeUuid: quoteProductData.storeUuid,
        isAddOn: quoteProductData.value.products.find((row: any) => row.productVariantUuid === item.productVariantUuid)?.isAddOn ?? false,
        baseProductUuidForAddOn: quoteProductData.value.products.find((row: any) => row.isAddOn === false)?.productUuid ?? productuuid,
        option1: item.option1,
        option2: item.option2,
        option3: item.option3
      }));

      // Create the final request array by adding selected add-ons
      const request = selectedAddOn.reduce((acc: any[], item: Product) => {
        // Check if this product already exists in the accumulator
        const existingProductIndex = acc.findIndex(
          (product) => product.productUuid === item.uuid && product.productVariantUuid === item.productVariantUuid
        );

        if (existingProductIndex !== -1) {
          // If product exists, increment its quantity
          acc[existingProductIndex].quantity += 1;
        } else {
          // If product doesn't exist, add it to the accumulator
          acc.push({
            bookingDate,
            productUuid: item.uuid,
            productVariantUuid: item.productVariantUuid,
            quantity: 1,
            rentalDuration: quoteProductData.value.rentalDuration,
            rentalType: quoteProductData.rentalType,
            retailerUuid: quoteProductData.value.retailerUuid,
            storeUuid: quoteProductData.storeUuid,
            isAddOn: true,
            baseProductUuidForAddOn: quoteProductData.value.products.find((row: any) => row.isAddOn === false)?.productUuid ?? productuuid,
            ...quoteId && { previousQuoteUuid: quoteId }
          });
        }

        return acc;
      }, baseProducts);

      await productDetailsClient
        .getQuotesCharges({ products: request })
        .then(async (data: any) => {
          
          if (data.updateQuoteProduct && data.productAvailibility) {
            const quoteProductsData = data.productRates.map((item: any) => {
              // Find the original product to get its options
              const originalProduct = request.find((p: any) => 
                p.productUuid === item.productUuid && 
                p.productVariantUuid === item.productVariantUuid
              );
              
              return {
                bookingDate,
                productName: item.productName,
                productUuid: item.productUuid,
                productVariantUuid: item.productVariantUuid,
                productRetailValue: item.productRetailValue,
                productQuantity: item.quantity,
                productRentalTerm: item.rentalTerm,
                rentalType: quoteProductData.rentalType,
                productRate: item.productRate,
                isAddOn: item.isAddOn,
                option1: originalProduct?.option1,
                option2: originalProduct?.option2,
                option3: originalProduct?.option3
              };
            });

            const payload = {
              quoteTotal: data.orderValue,
              rentalDuration: quoteProductData.value.rentalDuration,
              rentalType: data.rentalType,
              retailerUuid: quoteProductData.value.retailerUuid,
              serviceCharge: data.serviceCharge,
              storeUuid: quoteProductData.storeUuid,
              deliveryCharge: data.deliveryCharge,
              depositAmount: data.depositCharge,
              instoreCollectionCharge: data.instoreCollectionCharge,
              products: quoteProductsData
            }

            await productDetailsClient
              .createQuote(payload)
              .then((res: any) => {
                setQuoteCookies(res.quote.uuid);
                const bookingDate = JSON.stringify({
                  startDate: moment.parseZone(quoteProductData.startDate).format('YYYY-MM-DDTHH:mm:ss'),
                  endDate: moment.parseZone(quoteProductData.endDate).format('YYYY-MM-DDTHH:mm:ss'),                  
                  rentPrice: quoteProductData.rentPrice,
                });                
                localStorage.setItem("bookingDate", bookingDate);
                onHide();
                if (redirectToCart) {
                  navigate("/cart");
                } else {
                  navigate(`/product?id=${productUuid}`);
                }
              })
              .catch((error: any) => {
                console.log(error)
                toast.error(error?.message);
              });
          } else {
            toast.error("Some thing went wrong");
          }
        })
        .catch((error) => {
          toast.error(error?.message);
        });
    }
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size={modalSize}
      aria-labelledby="add-ons-modal"
      centered
      className="add-ons-modal"
    >
      <Modal.Header>
        <Modal.Title id="add-ons-modal">Select Add-ons</Modal.Title>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={onHide}
        >
          <X size={24} />
        </button>
      </Modal.Header>
      <Modal.Body>
        <div className="add-ons-container" ref={containerRef}>
          {recommendedProducts && recommendedProducts?.productsList.length > 0 && recommendedProducts?.productsList?.map((addOn) => (
            <div className="col-lg-4 col-md-4 col-sm-5  col-7">
              <div key={addOn.id} className="card product-card border-1 rounded-3 shadow-sm">
                <div className="position-relative ">
                  <div className="overflow-hidden product-image-col">
                    {addOn.productImageUrl ?
                      <img src={addOn.productImageUrl} alt={addOn.productName} className="card-img-top product-image rounded-0 rounded-md-3 rounded-bottom-0" /> :

                      <PlaceHolderStoreImage className="placeholder-product-image" />

                    }
                  </div>

                </div>
                <div className="card-body p-3">
                  <h5 className="card-title mb-3 fw-medium">{addOn.productName}</h5>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="price">
                      {addOn.productRentalPriceDescription}</span>
                  </div>


                  <div className="add-on-actions">
                    <button
                      className={`btn btn-outline-secondary btn-sm px-2 px-md-3 rounded-pill ${selectedAddOn.includes(addOn) ? "selected-btn" : ""}`}

                      onClick={() => {
                        if (addOn?.isDefaultVariant) {
                          if (selectedAddOn.includes(addOn)) {
                            setSelectedAddOn(selectedAddOn.filter(item => item.uuid !== addOn.uuid));
                          } else {
                            setSelectedAddOn([...selectedAddOn, addOn]);
                          }
                        } else {
                          onSubmit(false, addOn.uuid);
                          onHide();
                        }
                      }}
                    >{addOn?.isDefaultVariant ? selectedAddOn.includes(addOn)
                      ? "Selected" : "Select" : 'View available options'}
                    </button>
                    {/* <Button
                    className="add-to-cart-btn"
                    btnText="Add to Cart"
                    onClick={() => handleAddToCart(addOn.id)}
                  /> */}
                  </div>
                </div>
              </div>
            </div>

          ))}
          <div ref={loaderRef} className="loader-element">
            {loading && (
              <div className="loading-indicator text-center py-3 d-inline-block">
                <p>Loading more...</p>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="view-cart-btn footer-btn"
          onClick={() => onSubmit(true)}
          btnText={selectedAddOn.length > 0 ? "Add to Cart" : "Skip and Go to Cart"}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default AddOnsModal;
